/**
 * TradeBuilder - Full-page modal for creating or countering a trade
 *
 * Split-panel layout showing both sides of the trade:
 * - "You Give" (left): NFTs from your wallet + optional XRP
 * - "You Want" (right): NFTs from recipient's wallet + optional XRP
 *
 * Entry points: NFT detail page, Wallet page, Inbox, /trades page
 */

import React, { useState, useEffect, useCallback } from 'react';
import { FeatherX, FeatherPlus, FeatherSearch } from '@subframe/core';
import { useAuthStore } from '../../store/authStore';
import { apiClient, ApiResponse } from '../../api/client';
import { getPublicWalletOfferControls } from '../../api/settings';
import { createTrade, counterTrade, getTradeExpirationOptions } from '../../api/trades';
import type { TradeExpirationOption, NFTTrade } from '../../api/trades';
import { formatAddress } from '../../utils/formatters';
import { dropsToXrp } from '../../utils/currency';
import NFTPicker from './NFTPicker';
import MultiCollectionWarning from './MultiCollectionWarning';
import { is3DModelUrl } from '../../utils/media';

interface NFTForTrade {
  nft_token_id: string;
  name?: string | null;
  image_url?: string | null;
  thumbnail_url?: string | null;
  rarity_rank?: number | null;
  collection_name?: string | null;
  collection_id?: string | null;
  floor_price_drops?: string | null;
  in_bundle?: boolean;
  in_trade?: boolean;
}

interface TradeBuilderProps {
  isOpen: boolean;
  onClose: () => void;
  onTradeCreated: (trade: NFTTrade) => void;
  // Pre-populated fields
  recipientAddress?: string;
  preSelectedRecipientNfts?: NFTForTrade[];
  // Counter mode
  counterTradeId?: string;
  counterInitiatorNfts?: NFTForTrade[];
  counterRecipientNfts?: NFTForTrade[];
  counterInitiatorXrp?: number;
  counterRecipientXrp?: number;
  // For counter mode: indicates if the current user is the original trade initiator
  // Used to correctly map "my NFTs" and "their NFTs" to initiator/recipient when submitting
  isUserInitiator?: boolean;
}

export default function TradeBuilder({
  isOpen,
  onClose,
  onTradeCreated,
  recipientAddress: initialRecipient,
  preSelectedRecipientNfts,
  counterTradeId,
  counterInitiatorNfts,
  counterRecipientNfts,
  counterInitiatorXrp,
  counterRecipientXrp,
  isUserInitiator = true, // Default to true for new trades where user is the initiator
}: TradeBuilderProps) {
  const { user } = useAuthStore();
  const myAddress = user?.xrpl_address || '';

  const [recipientAddress, setRecipientAddress] = useState(initialRecipient || '');
  const [myNfts, setMyNfts] = useState<NFTForTrade[]>(counterInitiatorNfts || []);
  const [theirNfts, setTheirNfts] = useState<NFTForTrade[]>(
    preSelectedRecipientNfts || counterRecipientNfts || []
  );
  const [myXrp, setMyXrp] = useState<string>(counterInitiatorXrp?.toString() || '');
  const [theirXrp, setTheirXrp] = useState<string>(counterRecipientXrp?.toString() || '');
  const [message, setMessage] = useState('');
  const [expiration, setExpiration] = useState<TradeExpirationOption>('7_days');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // NFT picker state
  const [showMyPicker, setShowMyPicker] = useState(false);
  const [showTheirPicker, setShowTheirPicker] = useState(false);

  // Recipient trade offer controls
  const [recipientTradesDisabled, setRecipientTradesDisabled] = useState(false);
  const [checkingRecipient, setCheckingRecipient] = useState(false);

  const isCounterMode = !!counterTradeId;
  const expirationOptions = getTradeExpirationOptions();

  // Check if recipient has disabled incoming trade offers
  useEffect(() => {
    if (!recipientAddress || recipientAddress.length < 25 || isCounterMode) {
      setRecipientTradesDisabled(false);
      return;
    }

    let cancelled = false;
    setCheckingRecipient(true);
    getPublicWalletOfferControls(recipientAddress)
      .then((controls) => {
        if (!cancelled) {
          setRecipientTradesDisabled(controls.wallet_trade_offers_disabled);
        }
      })
      .catch(() => {
        if (!cancelled) setRecipientTradesDisabled(false);
      })
      .finally(() => {
        if (!cancelled) setCheckingRecipient(false);
      });

    return () => { cancelled = true; };
  }, [recipientAddress, isCounterMode]);

  // XRP validation: only one side can have XRP
  const myXrpValue = parseFloat(myXrp) || 0;
  const theirXrpValue = parseFloat(theirXrp) || 0;

  const canAddMyXrp = theirXrpValue === 0;
  const canAddTheirXrp = myXrpValue === 0;

  // Validation
  const hasAtLeastOneNft = myNfts.length > 0 || theirNfts.length > 0;
  const isValid =
    recipientAddress &&
    recipientAddress !== myAddress &&
    hasAtLeastOneNft &&
    myNfts.length <= 10 &&
    theirNfts.length <= 10 &&
    !recipientTradesDisabled;

  const handleRemoveMyNft = (tokenId: string) => {
    setMyNfts((prev) => prev.filter((n) => n.nft_token_id !== tokenId));
  };

  const handleRemoveTheirNft = (tokenId: string) => {
    setTheirNfts((prev) => prev.filter((n) => n.nft_token_id !== tokenId));
  };

  const handleSubmit = async () => {
    if (!isValid || isSubmitting) return;
    setError(null);
    setIsSubmitting(true);

    try {
      let result;

      if (isCounterMode && counterTradeId) {
        // In counter mode, we need to map "my" and "their" NFTs back to
        // initiator/recipient based on who the user is in the original trade.
        // The API always expects initiatorNftTokenIds = what the original initiator gives
        // and recipientNftTokenIds = what the original recipient gives.
        const initiatorNfts = isUserInitiator ? myNfts : theirNfts;
        const recipientNfts = isUserInitiator ? theirNfts : myNfts;
        const initiatorXrp = isUserInitiator ? myXrpValue : theirXrpValue;
        const recipientXrp = isUserInitiator ? theirXrpValue : myXrpValue;

        result = await counterTrade(counterTradeId, {
          initiatorNftTokenIds: initiatorNfts.map((n) => n.nft_token_id),
          recipientNftTokenIds: recipientNfts.map((n) => n.nft_token_id),
          initiatorXrpAmount: initiatorXrp || undefined,
          recipientXrpAmount: recipientXrp || undefined,
          message: message || undefined,
        });
      } else {
        // New trade: user is always the initiator
        result = await createTrade({
          recipientAddress,
          initiatorNftTokenIds: myNfts.map((n) => n.nft_token_id),
          recipientNftTokenIds: theirNfts.map((n) => n.nft_token_id),
          initiatorXrpAmount: myXrpValue || undefined,
          recipientXrpAmount: theirXrpValue || undefined,
          message: message || undefined,
          expiration,
        });
      }

      onTradeCreated(result.trade);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to create trade');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-default-background rounded-md border border-solid border-neutral-border shadow-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-solid border-neutral-border">
          <h2 className="text-body-bold font-body-bold text-default-font">
            {isCounterMode ? 'Counter Trade' : 'Propose a Trade'}
          </h2>
          <button onClick={onClose} className="p-1 hover:bg-neutral-100 rounded-md">
            <FeatherX className="h-5 w-5 text-subtext-color" />
          </button>
        </div>

        {/* Recipient address */}
        {!isCounterMode && (
          <div className="px-4 pt-4">
            <label className="text-sm font-medium text-default-font">Trading with</label>
            <input
              type="text"
              value={recipientAddress}
              onChange={(e) => setRecipientAddress(e.target.value)}
              placeholder="Enter wallet address (r...)"
              disabled={!!initialRecipient}
              className="mt-1 w-full px-3 py-2 border border-neutral-border rounded-md text-sm text-default-font bg-default-background focus:outline-none focus:ring-2 focus:ring-brand-primary disabled:bg-neutral-50 disabled:text-subtext-color"
            />
          </div>
        )}

        {recipientTradesDisabled && (
          <div className="mx-4 mt-3">
            <div className="text-sm text-amber-600 bg-amber-950/30 border border-amber-800/50 rounded-md px-3 py-2">
              This user has disabled incoming trade offers.
            </div>
          </div>
        )}

        {isCounterMode && (
          <div className="px-4 pt-4 text-sm text-subtext-color">
            Counter-offer for trade with{' '}
            <span className="font-medium text-default-font">{formatAddress(recipientAddress)}</span>
          </div>
        )}

        {/* Split panels */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4">
          {/* YOU GIVE */}
          <div className="border border-solid border-neutral-border rounded-md p-3">
            <div className="text-sm font-semibold text-default-font mb-3">YOU GIVE</div>

            {/* NFT list */}
            <div className="space-y-2 mb-3">
              {myNfts.map((nft) => (
                <div
                  key={nft.nft_token_id}
                  className="flex items-center gap-2 p-2 bg-neutral-50 rounded-md"
                >
                  <div className="w-8 h-8 rounded bg-neutral-100 overflow-hidden flex-shrink-0">
                    {(nft.thumbnail_url || nft.image_url) ? (
                      is3DModelUrl(nft.image_url) ? (
                        <div className="w-full h-full flex items-center justify-center bg-neutral-100">
                          <span style={{ fontSize: '14px' }}>🧊</span>
                        </div>
                      ) : (
                        <img
                          src={nft.thumbnail_url || nft.image_url || ''}
                          alt=""
                          className="w-full h-full object-cover"
                        />
                      )
                    ) : null}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium text-default-font truncate">
                      {nft.name || 'Unknown NFT'}
                    </div>
                    {nft.collection_name && (
                      <div className="text-xs text-subtext-color truncate">
                        {nft.collection_name}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => handleRemoveMyNft(nft.nft_token_id)}
                    className="p-0.5 hover:bg-neutral-200 rounded"
                  >
                    <FeatherX className="h-3.5 w-3.5 text-subtext-color" />
                  </button>
                </div>
              ))}
            </div>

            {myNfts.length < 10 && (
              <button
                onClick={() => setShowMyPicker(true)}
                className="w-full flex items-center justify-center gap-1.5 px-3 py-2 border border-dashed border-neutral-border rounded-md text-xs text-subtext-color hover:bg-neutral-50 transition-colors"
              >
                <FeatherPlus className="h-3.5 w-3.5" />
                Add NFT
              </button>
            )}

            {/* XRP sweetener */}
            <div className="mt-3">
              <label className="text-xs text-subtext-color">+ XRP</label>
              <input
                type="number"
                value={myXrp}
                onChange={(e) => setMyXrp(e.target.value)}
                placeholder="0"
                min="0"
                step="1"
                disabled={!canAddMyXrp}
                className="mt-1 w-full px-3 py-1.5 border border-neutral-border rounded-md text-sm text-default-font bg-default-background focus:outline-none focus:ring-2 focus:ring-brand-primary disabled:bg-neutral-50 disabled:text-subtext-color"
              />
              {parseFloat(myXrp) > 0 && (
                <div className="mt-1 text-xs text-subtext-color">
                  3% fee ({(parseFloat(myXrp) * 0.03).toFixed(2)} XRP)
                </div>
              )}
            </div>
          </div>

          {/* YOU WANT */}
          <div className="border border-solid border-neutral-border rounded-md p-3">
            <div className="text-sm font-semibold text-default-font mb-3">YOU WANT</div>

            {/* NFT list */}
            <div className="space-y-2 mb-3">
              {theirNfts.map((nft) => (
                <div
                  key={nft.nft_token_id}
                  className="flex items-center gap-2 p-2 bg-neutral-50 rounded-md"
                >
                  <div className="w-8 h-8 rounded bg-neutral-100 overflow-hidden flex-shrink-0">
                    {(nft.thumbnail_url || nft.image_url) ? (
                      is3DModelUrl(nft.image_url) ? (
                        <div className="w-full h-full flex items-center justify-center bg-neutral-100">
                          <span style={{ fontSize: '14px' }}>🧊</span>
                        </div>
                      ) : (
                        <img
                          src={nft.thumbnail_url || nft.image_url || ''}
                          alt=""
                          className="w-full h-full object-cover"
                        />
                      )
                    ) : null}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium text-default-font truncate">
                      {nft.name || 'Unknown NFT'}
                    </div>
                    {nft.collection_name && (
                      <div className="text-xs text-subtext-color truncate">
                        {nft.collection_name}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => handleRemoveTheirNft(nft.nft_token_id)}
                    className="p-0.5 hover:bg-neutral-200 rounded"
                  >
                    <FeatherX className="h-3.5 w-3.5 text-subtext-color" />
                  </button>
                </div>
              ))}
            </div>

            {theirNfts.length < 10 && recipientAddress && (
              <button
                onClick={() => setShowTheirPicker(true)}
                className="w-full flex items-center justify-center gap-1.5 px-3 py-2 border border-dashed border-neutral-border rounded-md text-xs text-subtext-color hover:bg-neutral-50 transition-colors"
              >
                <FeatherPlus className="h-3.5 w-3.5" />
                Add NFT
              </button>
            )}

            {/* XRP sweetener */}
            <div className="mt-3">
              <label className="text-xs text-subtext-color">+ XRP</label>
              <input
                type="number"
                value={theirXrp}
                onChange={(e) => setTheirXrp(e.target.value)}
                placeholder="0"
                min="0"
                step="1"
                disabled={!canAddTheirXrp}
                className="mt-1 w-full px-3 py-1.5 border border-neutral-border rounded-md text-sm text-default-font bg-default-background focus:outline-none focus:ring-2 focus:ring-brand-primary disabled:bg-neutral-50 disabled:text-subtext-color"
              />
              {parseFloat(theirXrp) > 0 && (
                <div className="mt-1 text-xs text-subtext-color">
                  3% fee ({(parseFloat(theirXrp) * 0.03).toFixed(2)} XRP)
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Message & Expiration */}
        <div className="px-4 pb-2 space-y-3">
          <div>
            <label className="text-sm font-medium text-default-font">Message (optional)</label>
            <input
              type="text"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Add a note to your trade..."
              maxLength={500}
              className="mt-1 w-full px-3 py-2 border border-neutral-border rounded-md text-sm text-default-font bg-default-background focus:outline-none focus:ring-2 focus:ring-brand-primary"
            />
          </div>

          {!isCounterMode && (
            <div>
              <label className="text-sm font-medium text-default-font">Expires</label>
              <select
                value={expiration}
                onChange={(e) => setExpiration(e.target.value as TradeExpirationOption)}
                className="mt-1 w-full px-3 py-2 border border-neutral-border rounded-md text-sm text-default-font bg-default-background focus:outline-none focus:ring-2 focus:ring-brand-primary"
              >
                {expirationOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Error */}
        {error && (
          <div className="px-4 pb-2">
            <div className="text-sm text-error-700 bg-error-50 border border-error-200 rounded-md px-3 py-2">
              {error}
            </div>
          </div>
        )}

        {/* Multi-collection warning */}
        <MultiCollectionWarning
          sideA={myNfts}
          sideB={theirNfts}
          className="mx-4 mb-2"
        />

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-4 border-t border-solid border-neutral-border">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-default-font bg-default-background border border-neutral-border rounded-md hover:bg-neutral-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!isValid || isSubmitting}
            className="px-4 py-2 text-sm font-medium text-white bg-brand-primary rounded-md hover:bg-brand-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting
              ? 'Submitting...'
              : isCounterMode
                ? 'Send Counter'
                : 'Propose Trade'}
          </button>
        </div>
      </div>

      {/* NFT Picker Modals */}
      {showMyPicker && (
        <NFTPicker
          walletAddress={myAddress}
          onSelect={(nft) => {
            setMyNfts((prev) => {
              if (prev.find((n) => n.nft_token_id === nft.nft_token_id)) return prev;
              return [...prev, nft];
            });
            setShowMyPicker(false);
          }}
          onClose={() => setShowMyPicker(false)}
          excludeTokenIds={myNfts.map((n) => n.nft_token_id)}
        />
      )}

      {showTheirPicker && recipientAddress && (
        <NFTPicker
          walletAddress={recipientAddress}
          onSelect={(nft) => {
            setTheirNfts((prev) => {
              if (prev.find((n) => n.nft_token_id === nft.nft_token_id)) return prev;
              return [...prev, nft];
            });
            setShowTheirPicker(false);
          }}
          onClose={() => setShowTheirPicker(false)}
          excludeTokenIds={theirNfts.map((n) => n.nft_token_id)}
        />
      )}
    </div>
  );
}
